/* ═══════════════════════════════════════════════════════════════
   THE MASTER FORMA STUDIO — ground-up rebuild (docs/DESIGNER_UNIFIED_PLAN.md §3 R6).

   The master forma editor is a LAY PERSON'S tool for setting up a
   client's Word formatting rules. Built entirely from the Designer's
   proven stack — two-row header, §2.8 panel-button system, FormaDock
   workspace, universal undo, the shared body/zone-layout brains — so it
   inherits the same feel and the same guarantee: WYSIWYG IS LAW, the
   paper on screen is exactly what word-engine prints.

   Phase A (this file): the shell + the WYSIWYG canvas. Import, export,
   CSI-defaults UI, EOS controls beyond the basics, and mixed-size zone
   text land in phase B — see the header comment for the full list.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { Prefs } from '../services/prefsService';
import { usePref } from '../lib/usePref';
import { setRecordTitle } from '../routing/addressSync';
import { FirmPrefs } from '../services/firmPrefsService';
import { FormaService } from '../services/formaService';
import { EditorHistoryPanel } from '../components/EditorComponents';
import EmptyState from '../components/EmptyState';
import type { FormaDockHandle } from '../components/FormaDock';
import { Icon } from '../components/ui/Icon';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { useUndoCoordinator } from '../editor-chrome/undoCoordinator';
import {
  DesignerToolbar,
  ToolbarButton,
  type PanelEntry,
  type ToolbarItem,
} from '../editor-chrome/Toolbar';
import { UniversalHistory } from '../editor-chrome/ToolbarControls';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import { pageVariants, type PageVariant } from './formaZonesModel';
import { fillFormaCsiGaps } from '../../shared/csi-defaults';
import { hasPermission } from '../../shared/permissions';
import '../editor-chrome/editor.css';
import '../editor-chrome/StudioShared.css';
import './Studio.css';
import { STUDIO_PALETTES, STUDIO_PANEL_ICONS, StudioWorkspace } from './StudioWorkspace';
import { StudioCanvas } from './StudioCanvas';
import { FormatBar } from './FormatBar';
import { useFormaSelection } from './useFormaSelection';
import { useMasterFormaDraft } from './useMasterFormaDraft';
import { FormaChip } from './FormaChip';
import { PeerChips } from '../spec-studio/PeerChips';
import { ViewModeContext, type ViewModeValue } from '../spec-studio/useViewModeGuard';
import { liveBlockMessage } from '../spec-studio/collabState';
import {
  discardDraftBody,
  endedPlan,
  formaChipLabel,
  peerNames,
  pickExportSource,
  publishConfirmBody,
  type FormaChipState,
} from './formaDraftModel';
import { AttachChip } from './AttachChip';
import { ZoomControl } from '../components/ui/ZoomControl';

// Cohesion R1 Phase 4 zoom law: 50–180% everywhere.
// UI Scale (DESIGN.md §3.6): `zoom` here is RELATIVE to the dial (1.0 =
// "matches surroundings") — the canvas multiplies it by the dial to get
// the applied transform. Storage/keys are unchanged: every value saved so
// far was captured at dial 1.00, so a saved number IS the relative value
// already — no migration/conversion needed.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.8;
const ZOOM_KEY = 'formaStudio.zoom';

/** Automated capture/QA runs sign in as a real user; without this they would
    overwrite that user's CLOUD zoom with a throwaway one (FormaDock.tsx has
    the same guard for dock layouts). Local persistence still works so a
    harness can test restore within its own profile. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/** Where the firm's default Studio layout lives (contract §2.9). ONE
 *  constant — swapping the out-of-box default is a one-line change. */
export const STUDIO_FIRM_LAYOUT_KEY = 'sf.formaStudioDock.firmDefault.v1';

/** The one draft chip (shared with the Designer's project forma). */
const SaveIndicator = FormaChip;

export interface StudioProps {
  /** Null only for a brand-new, not-yet-saved forma — pass `newForma`
   *  alongside it (contract §3 R6, item 10a). */
  formaId: string | null;
  /** A CSI-seeded, in-memory config to start from when `formaId` is null. */
  newForma?: StandardConfig | null;
  onBack: () => void;
  organizations: Array<{ id: string; name?: string; shortName?: string }>;
  /** For "Export to project" (contract §3 R6, item 3) — masters only. */
  projects: Array<{ id: string; name?: string; projectNumber?: string }>;
  canSetFirmDefault?: boolean;
  toast: (msg: string, isError?: boolean) => void;
  isDark?: boolean;
  userId?: string;
  userName: string;
  userPermissions?: string[];
  /** Mirrors a save or an "Export to project" copy into the caller's own
   *  forma list — the same optimistic update the classic editor does
   *  locally, so a brand-new forma shows up in the Formas library the
   *  moment you go back, without waiting on the live-sync round trip. */
  onFormaPersisted?: (forma: StandardConfig) => void;
  /** Hands the shell a stable "publish now" trigger while the Studio is
   *  mounted (null on unmount) — the native File-menu Ctrl+S accelerator
   *  routes through this, same as the in-app Save button. */
  registerSaveHandler?: (fn: (() => void) | null) => void;
}

export function Studio({
  formaId,
  newForma,
  onBack,
  organizations,
  projects,
  onFormaPersisted,
  registerSaveHandler,
  canSetFirmDefault = false,
  toast,
  isDark = false,
  userId,
  userName,
  userPermissions,
}: StudioProps) {
  const canImportTemplate = hasPermission(userPermissions, 'formas.import_template');
  const canExportTemplate = hasPermission(userPermissions, 'formas.export_template');
  const canEditForma = hasPermission(userPermissions, 'formas.edit');
  const canPublishForma = hasPermission(userPermissions, 'formas.publish');
  // Opens in Edit for anyone who may edit (the forma Studio always opened
  // straight into editing); viewers get View only.
  const [mode, setMode] = React.useState<'view' | 'edit'>(canEditForma ? 'edit' : 'view');
  const editing = mode === 'edit' && canEditForma;
  const editBtnRef = React.useRef<HTMLButtonElement>(null);
  const nudgeEdit = React.useCallback(() => {
    if (!canEditForma) return;
    const el = editBtnRef.current;
    if (!el) return;
    el.classList.remove('sps-edit-nudge');
    void el.offsetWidth; // reflow so a second press restarts the ring
    el.classList.add('sps-edit-nudge');
  }, [canEditForma]);
  const vm = React.useMemo<ViewModeValue>(
    () => ({ editing, canEdit: canEditForma, nudgeEdit }),
    [editing, canEditForma, nudgeEdit]
  );
  const onEnded = React.useCallback(
    ({ reset, own }: { reset: boolean; own: boolean }) => {
      if (endedPlan(reset).mode === 'view') setMode('view');
      toast(
        own
          ? 'Draft discarded'
          : reset
            ? 'This forma was refreshed - showing the published version'
            : 'A teammate ended this draft - showing the published version'
      );
    },
    [toast]
  );
  const draft = useMasterFormaDraft(formaId, userName, userId, newForma, {
    editing,
    canEdit: canEditForma,
    canPublish: canPublishForma,
    onEnded,
  });
  const forma = draft.forma;
  const blockedReason = draft.blocked;
  React.useEffect(() => {
    if (!blockedReason) return;
    setMode('view');
    toast(liveBlockMessage(blockedReason), true);
  }, [blockedReason, toast]);

  /* ─── Universal undo (§2.7) — one domain today ('forma'), architected the
     same way the Designer joins two, so phase B's editable surfaces slot in
     without a rewrite. */
  const undoTimeline = useUndoCoordinator();
  React.useEffect(() => {
    undoTimeline.register('forma', {
      undo: () => draft.undo(),
      redo: () => draft.redo(),
    });
    return () => undoTimeline.register('forma', null);
  }, [undoTimeline, draft]);
  const resetTimeline = undoTimeline.reset;
  React.useEffect(() => resetTimeline(), [formaId, resetTimeline]);

  const editNoted = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig) => {
      if (!editing) {
        nudgeEdit(); // View mode: every edit is inert and points at Edit
        return;
      }
      draft.edit(fn);
      undoTimeline.note('forma');
    },
    [draft, undoTimeline, editing, nudgeEdit]
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea)$/i.test(target.tagName)) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        void handlePublishRef.current();
        return;
      }
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      e.preventDefault();
      if (isUndo) undoTimeline.undo();
      else undoTimeline.redo();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [undoTimeline]);

  const [publishOpen, setPublishOpen] = React.useState(false);
  const runPublish = React.useCallback(async (): Promise<StandardConfig | undefined> => {
    try {
      const saved = await draft.publish();
      if (saved) onFormaPersisted?.(saved);
      toast('Published');
      return saved;
    } catch (err) {
      toast(
        'Publish failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown')),
        true
      );
      return undefined;
    }
  }, [draft, onFormaPersisted, toast]);
  const handlePublish = React.useCallback(async () => {
    if (!editing) {
      nudgeEdit();
      return;
    }
    if (!forma?.docSettings?.standardName?.trim()) {
      toast('Enter a forma name first', true);
      return;
    }
    // Others in the draft: publishing is for everyone's edits - say so first.
    if (draft.peers.length > 0) {
      setPublishOpen(true);
      return;
    }
    await runPublish();
  }, [editing, nudgeEdit, forma, draft.peers.length, runPublish, toast]);
  const handlePublishRef = React.useRef(handlePublish);
  handlePublishRef.current = handlePublish;
  React.useEffect(() => {
    registerSaveHandler?.(() => void handlePublishRef.current());
    return () => registerSaveHandler?.(null);
  }, [registerSaveHandler]);

  /* ─── Import as draft (contract §3 R6, item 1) — same IPC as the classic
     editor's handleImport (app.tsx). CSI gap-fill runs on the harvest so an
     import that left spacing/indents unsaid starts CSI-correct instead of
     0 (item 10b) — imported values always win where present. */
  const handleImport = React.useCallback(async () => {
    if (!window.api?.openDotxDialog || !window.api?.importTemplate) {
      toast('Import requires the desktop app', true);
      return;
    }
    const filePath = await window.api.openDotxDialog();
    if (!filePath) return;
    try {
      const result = await window.api.importTemplate(filePath);
      if (!result.success || !result.config) {
        toast('Import failed: ' + (result.error || 'Unknown error'), true);
        return;
      }
      draft.importDraft(fillFormaCsiGaps(result.config));
      undoTimeline.reset();
      toast('Imported — review and Publish to add it to your Master Formas');
    } catch (err) {
      toast('Import error: ' + (err instanceof Error ? err.message : String(err)), true);
    }
  }, [draft, toast, undoTimeline]);

  /* ─── Export .dotx (round-trip) — same shape the classic editor's
     handleExport builds (app.tsx ~1929). ─── */
  const handleExportDotx = React.useCallback(async () => {
    if (!window.api?.saveDotxDialog || !window.api?.createTemplate) {
      toast('Export requires the desktop app', true);
      return;
    }
    // Every road OUT reads the PUBLISHED forma, never the shared draft; a
    // never-published forma publishes first (same rule as Export to project).
    const pick = pickExportSource({
      published: draft.published,
      draftForma: draft.forma,
      unsaved: draft.unsaved,
    });
    let src: StandardConfig | null = pick.source;
    if (pick.publishFirst) src = (await runPublish()) ?? null;
    if (!src) {
      toast('Publish the forma first, then export it', true);
      return;
    }
    const forma = src;
    const name =
      (forma.docSettings?.standardName || 'template').replace(/[^a-zA-Z0-9_-]/g, '_') + '.dotx';
    const savePath = await window.api.saveDotxDialog(name);
    if (!savePath) return;
    try {
      const cfg: StandardConfig = {
        ...forma,
        images: forma.images && Object.keys(forma.images).length > 0 ? forma.images : undefined,
        placeholders:
          forma.placeholders && Object.keys(forma.placeholders).length > 0
            ? forma.placeholders
            : undefined,
        bindings:
          forma.bindings && Object.keys(forma.bindings).length > 0 ? forma.bindings : undefined,
      };
      const result = await window.api.createTemplate(cfg, savePath);
      if (result.success) toast('Template exported');
      else toast('Export failed', true);
    } catch (err) {
      toast('Export error: ' + (err instanceof Error ? err.message : String(err)), true);
    }
  }, [draft, runPublish, toast]);

  /* ─── Export to project (contract §3 R6, item 3) — same copy-on-write
     model as the classic editor's "Export to project" (app.tsx ~2843):
     the project gets its OWN copy; this master stays untouched. Saves
     first only for a brand-new, never-created forma — otherwise the copy is
     made from the PUBLISHED master, never a pending draft. ─── */
  const handleExportToProject = React.useCallback(
    async (projectId: string) => {
      if (!projectId || !forma) return;
      const project = projects.find((p) => p.id === projectId);
      try {
        let master = pickExportSource({
          published: draft.published,
          draftForma: draft.forma,
          unsaved: draft.unsaved,
        }).source;
        if (draft.unsaved) {
          const created = await runPublish();
          if (!created) return;
          master = created;
        }
        if (!master?.id) {
          toast('Publish the forma first, then export it to a project', true);
          return;
        }
        const copy = await FormaService.ensureProjectCopy(master, {
          id: projectId,
          number: project?.projectNumber,
          name: project?.name,
        });
        onFormaPersisted?.(copy);
        toast(`Exported to ${project?.name || 'project'} — the project now has its own copy`);
      } catch (err) {
        toast('Export failed: ' + (err instanceof Error ? err.message : String(err)), true);
      }
    },
    [draft, forma, runPublish, onFormaPersisted, projects, toast]
  );

  const [discardOpen, setDiscardOpen] = React.useState(false);
  // The shared draft is kept on the server, so leaving never loses it: the
  // exit guard is only for a brand-new forma that exists nowhere else yet.
  const [discardDraftOpen, setDiscardDraftOpen] = React.useState(false);
  const leave = React.useCallback(() => {
    if (draft.unsaved) {
      setDiscardOpen(true);
      return;
    }
    onBack();
  }, [draft.unsaved, onBack]);

  /* ─── §2.8 panel buttons: click = anchored dropdown, drag = dock/float ─── */
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [panelDragId, setPanelDragId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);

  const openTool = React.useCallback(
    (id: string) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      floatTool(id);
    },
    [dockPanels, floatTool]
  );

  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );
  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);

  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );

  /** Open a panel by id from somewhere other than its toolbar button — the
   *  paper's end-of-section line opens "Section End" this way, restoring the
   *  classic canvas's onEditSpecial('{EndOfSection}') shortcut. Anchors on
   *  the real button so the dropdown lands where the user expects it. */
  const openPanelById = React.useCallback(
    (id: string) => {
      const btn = document.querySelector<HTMLElement>(`.dsg-panel-btn[data-panel-id="${id}"]`);
      const r = btn?.getBoundingClientRect();
      onPanelClick(id, r ? { left: r.left, bottom: r.bottom } : { left: 24, bottom: 96 });
    },
    [onPanelClick]
  );

  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return;
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  const undockToFloat = React.useCallback(
    (id: string, at: { left: number; top: number }) => {
      dockHandleRef.current?.hidePanel(id);
      floatTool(id, at);
    },
    [floatTool]
  );

  const setFirmDefault = React.useCallback(() => {
    const layout = dockHandleRef.current?.snapshotLayout();
    if (!layout) {
      toast('The workspace is still opening — try again in a moment.', true);
      return;
    }
    FirmPrefs.set(STUDIO_FIRM_LAYOUT_KEY, layout, userName)
      .then(() =>
        toast('Saved as the firm default — everyone who has not customised will see this layout')
      )
      .catch((err: unknown) =>
        toast(
          'Could not save the firm default: ' +
            (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        )
      );
  }, [toast, userName]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      STUDIO_PALETTES.filter((p) => p.id !== 'canvas').map((p) => ({
        id: p.id,
        title: p.title,
        icon: STUDIO_PANEL_ICONS[p.id] ?? null,
        where: dockPanels.includes(p.id)
          ? 'docked'
          : floatingTools.some((t) => t.id === p.id)
            ? 'floating'
            : dropdown?.id === p.id
              ? 'dropdown'
              : null,
      })),
    [dockPanels, floatingTools, dropdown]
  );

  // ── Zone selection + level focus (ONE selection system, DESIGN §3.4) ──
  // Order 1 (Josh, 2026-08-13): the "Header & Footer" panel is gone —
  // selecting a zone band just marks it selected on the sheet; no panel to
  // open.
  // SYNC_PLAN §3.0: ONE selection (levels, header/footer tokens, End of
  // Section, the targeted band) owned here, shared by the paper, the panels
  // and the Format Bar. Clears on empty-paper click, Escape (its own single
  // listener) and a forma switch.
  const sel = useFormaSelection(forma);
  const clearSelection = sel.clear;
  React.useEffect(() => clearSelection(), [formaId, clearSelection]);
  const announceSelection = draft.setSelection;
  const selKeysForPeers = sel.keys;
  React.useEffect(() => {
    announceSelection(editing ? selKeysForPeers : []);
  }, [announceSelection, selKeysForPeers, editing]);
  const openLevelStyle = React.useCallback(
    (level: number, mods?: { ctrl?: boolean; shift?: boolean }) => {
      sel.selectLevel(level, mods);
      openTool('levels');
    },
    [openTool, sel]
  );

  const [pageVariant, setPageVariant] = React.useState<PageVariant>('odd');
  const pvs = pageVariants(forma);
  const effPageVariant = pvs.includes(pageVariant) ? pageVariant : 'odd';

  const [tokenDrag, setTokenDrag] = React.useState(false);
  const [dockLocked, setDockLocked] = usePref<boolean>(
    'sf.formaStudioDockLocked',
    'sf.formaStudioDockLocked',
    true,
    { parse: (raw) => raw !== '0', serialize: (v) => (v ? '1' : '0') }
  );
  const toggleDockLocked = React.useCallback(() => setDockLocked((v) => !v), [setDockLocked]);
  // Workspace ▸ View ▸ Pages / Continuous — per user, Continuous by default.
  const [paged, setPaged] = usePref<boolean>('sf.formaStudioPaged', 'sf.formaStudioPaged', false, {
    parse: (raw) => raw === '1',
    serialize: (v) => (v ? '1' : '0'),
  });

  // Pages view shows the true page: page 1 of a first-page-different forma
  // wears the First band, so ENTERING Pages mode defaults the edited variant
  // to `first` (not `odd`). After that the picker is free — the top band
  // always shows the variant the user picked. Continuous is untouched.
  const prevPagedRef = React.useRef(paged);
  React.useEffect(() => {
    if (paged && !prevPagedRef.current && pvs.includes('first')) setPageVariant('first');
    prevPagedRef.current = paged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged]);

  // Zoom persistence (Josh, 2026-08-13: "isn't saving persistently").
  // `Prefs` is a server-backed map that hydrates ASYNCHRONOUSLY after sign-in,
  // so the local value seeds first paint. Once `Prefs` lands, the cloud value
  // wins — same as designer/Designer.tsx (~605-614) and spec-studio/Studio.tsx
  // — so a stale value on THIS machine never outlives what was set elsewhere;
  // it no longer matters whether a local zoom already exists.
  const validZoom = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n >= ZOOM_MIN && n <= ZOOM_MAX ? n : null;
  };
  const [zoom, setZoom] = React.useState<number>(() => {
    let local: string | null = null;
    try {
      local = localStorage.getItem(ZOOM_KEY);
    } catch {
      /* private mode */
    }
    return validZoom(local) ?? validZoom(Prefs.get(ZOOM_KEY)) ?? 1;
  });

  // Re-render when prefs land from the server so the layout follows the user
  // across devices rather than being pinned to this machine's localStorage.
  React.useEffect(
    () =>
      Prefs.subscribe(() => {
        const remote = validZoom(Prefs.get(ZOOM_KEY));
        if (remote != null) setZoom(remote);
      }),
    []
  );
  const setZoomPersisted = (next: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(next.toFixed(2))));
    setZoom(clamped);
    if (!cloudPersistDisabled()) Prefs.set(ZOOM_KEY, clamped);
    try {
      localStorage.setItem(ZOOM_KEY, String(clamped));
    } catch {
      /* private mode */
    }
  };

  const [renaming, setRenaming] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState('');

  // Tab / window title: "<forma> · Formas · SpecForma".
  const recordName = forma ? forma.docSettings?.standardName || 'Untitled forma' : null;
  React.useEffect(() => {
    setRecordTitle(recordName);
    return () => setRecordTitle(null);
  }, [recordName]);

  const clientOptions = React.useMemo(
    () =>
      [
        ...new Set(organizations.map((o) => o.name || o.shortName).filter((n): n is string => !!n)),
      ].map((n) => ({
        value: n,
        label: n,
      })),
    [organizations]
  );

  const projectOptions = React.useMemo(
    () =>
      projects
        .filter((p) => p.name)
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map((p) => ({
          value: p.id,
          label: p.projectNumber ? `${p.projectNumber} — ${p.name}` : p.name || p.id,
        })),
    [projects]
  );

  // Publish needs the key once a shared draft exists, and only means
  // something when there is something pending (a brand-new forma: creating).
  const publishBlocked =
    !editing || (draft.live && (!canPublishForma || !draft.dirty)) || draft.blocked !== null;
  const publishHint = !editing
    ? 'Click Edit to make changes'
    : draft.live && !canPublishForma
      ? 'You need permission to publish formas'
      : draft.live && !draft.dirty
        ? 'Nothing to publish - this forma is up to date'
        : "Publish this forma - everyone's pending edits go live";
  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    if (!forma) return [];
    const items: ToolbarItem[] = [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        section: 'editing',
        node: <UniversalHistory timeline={undoTimeline} editor={null} />,
      },
      {
        id: 'zoom',
        label: 'Zoom',
        core: true,
        section: 'editing',
        node: (
          <ZoomControl
            value={Math.round(zoom * 100)}
            onChange={(pct) => setZoomPersisted(pct / 100)}
            min={Math.round(ZOOM_MIN * 100)}
            max={Math.round(ZOOM_MAX * 100)}
          />
        ),
      },
      {
        id: 'import',
        label: 'Import a Word file (.docx or .dotx) as a draft',
        section: 'document',
        node: (
          <ToolbarButton
            label="Import…"
            variant="outline"
            onClick={() => void handleImport()}
            tooltip="Import a Word template as a new, unsaved draft"
          />
        ),
      },
      {
        id: 'export',
        label: 'Export .dotx',
        section: 'document',
        node: (
          <ToolbarButton
            label="Export .dotx"
            variant="outline"
            onClick={() => void handleExportDotx()}
            tooltip="Export this forma as a Word template (.dotx)"
          />
        ),
      },
      {
        // Placed last before Save — same relative order as the classic
        // toolbar (history, Export, Save).
        id: 'history',
        label: 'Edit history',
        section: 'document',
        node: <EditorHistoryPanel entries={forma.editHistory || []} />,
      },
      {
        id: 'discard-draft',
        label: 'Discard draft',
        section: 'document',
        node: (
          <ToolbarButton
            label="Discard draft"
            variant="outline"
            onClick={() => setDiscardDraftOpen(true)}
            tooltip="Throw away the pending edits for everyone and go back to the published forma"
          />
        ),
      },
      {
        id: 'save',
        label: 'Publish',
        core: true,
        section: 'document',
        node: (
          <ToolbarButton
            label="Publish"
            variant="default"
            onClick={() => void handlePublish()}
            disabled={draft.saving || publishBlocked}
            tooltip={publishHint}
          />
        ),
      },
    ];
    return items.filter((item) => {
      if (item.id === 'discard-draft')
        return draft.live && editing && draft.dirty && canPublishForma;
      if (item.id === 'import') return canImportTemplate;
      if (item.id === 'export') return canExportTemplate;
      return true;
    });
  }, [
    forma,
    undoTimeline,
    zoom,
    draft.saving,
    draft.live,
    draft.dirty,
    editing,
    publishBlocked,
    publishHint,
    canPublishForma,
    handlePublish,
    handleImport,
    handleExportDotx,
    canImportTemplate,
    canExportTemplate,
  ]);

  if (draft.loadState === 'loading') {
    return (
      <div className="dsg-root dsg-loading sfs-state-root" aria-busy="true">
        <span className="sr-only">Opening the forma…</span>
        <div className="dsg-skeleton-sheet" />
      </div>
    );
  }

  if (draft.loadState === 'not-found' || draft.loadState === 'error' || !forma) {
    const notFound = draft.loadState === 'not-found';
    return (
      <div className="dsg-root dsg-error-root sfs-state-root">
        <EmptyState
          title={notFound ? 'This forma could not be found' : 'This forma could not be opened'}
          subtitle={
            notFound ? 'It may have been deleted.' : draft.error || 'Try again in a moment.'
          }
          action={
            notFound
              ? { label: 'Back to Master Formas', onClick: onBack }
              : { label: 'Try again', onClick: draft.reload }
          }
        />
      </div>
    );
  }

  return (
    <ViewModeContext.Provider value={vm}>
      <div className="dsg-root sfs-root">
        <header className="dsg-topbar">
          <div className="dsg-idrow">
            <button type="button" className="dsg-back" onClick={leave}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Master Formas
            </button>
            <nav className="dsg-crumb" aria-label="Breadcrumb">
              <span className="dsg-crumb-project">Master Formas</span>
              <span className="dsg-crumb-sep" aria-hidden="true">
                ▸
              </span>
              {renaming ? (
                <input
                  autoFocus
                  className="sfs-rename-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={() => {
                    setRenaming(false);
                    const v = nameDraft.trim();
                    if (v)
                      editNoted((f) => ({
                        ...f,
                        docSettings: { ...f.docSettings, standardName: v },
                      }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') setRenaming(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="dsg-crumb-spec"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'text',
                    padding: 0,
                    font: 'inherit',
                    color: 'inherit',
                  }}
                  onClick={() => {
                    setNameDraft(forma.docSettings?.standardName || '');
                    setRenaming(true);
                  }}
                  title="Rename this forma"
                >
                  <strong>{forma.docSettings?.standardName || 'Untitled forma'}</strong>
                </button>
              )}
            </nav>
            <AttachChip
              value={forma.docSettings?.clientName || ''}
              options={clientOptions}
              placeholder="Search clients…"
              emptyLabel="Add client"
              title="Attach this forma to a client (optional)"
              onChange={(v) => {
                const org = organizations.find((o) => (o.name || o.shortName) === v);
                editNoted((f) => ({
                  ...f,
                  docSettings: { ...f.docSettings, clientName: v },
                  clientId: org?.id,
                }));
              }}
            />
            {/* Export INTO a project — masters only (contract §3 R6, item 3).
             Same model as the classic editor: the project gets its own
             COPY; this master stays untouched. */}
            {!forma.projectId && (
              <AttachChip
                value=""
                options={projectOptions}
                placeholder="Search projects…"
                emptyLabel="Export to project"
                title="Copy this master forma into a project — the project gets its own separate forma; the master stays untouched"
                onChange={(v) => void handleExportToProject(v)}
              />
            )}
            <div className="dsg-idrow-status">
              <SaveIndicator state={draft.chip} />
              {editing && <PeerChips peers={draft.peers} />}
              {canEditForma &&
                (editing ? (
                  <button type="button" className="dsg-btn" onClick={() => setMode('view')}>
                    View published
                  </button>
                ) : (
                  <button
                    ref={editBtnRef}
                    type="button"
                    className="dsg-primary"
                    onClick={() => {
                      if (!draft.blocked) setMode('edit');
                    }}
                    onAnimationEnd={(e) => e.currentTarget.classList.remove('sps-edit-nudge')}
                    aria-disabled={draft.blocked ? true : undefined}
                    title={draft.blocked ? liveBlockMessage(draft.blocked) : undefined}
                  >
                    Edit
                  </button>
                ))}
            </div>
          </div>

          <div>
            <DesignerToolbar
              items={toolbarItems}
              panels={panelEntries}
              onPanelClick={onPanelClick}
              onPanelDragStart={setPanelDragId}
              onPanelDragEnd={onPanelDragEnd}
              dockLocked={dockLocked}
              onToggleDockLocked={toggleDockLocked}
              onResetLayout={() => dockHandleRef.current?.resetLayout()}
              onSetFirmDefault={setFirmDefault}
              canSetFirmDefault={canSetFirmDefault}
              userId={userId}
              storageKeyPrefix="sf.formaStudioToolbar.v1."
              pageView={paged ? 'pages' : 'continuous'}
              onPageView={(v) => setPaged(v === 'pages')}
            />
            <FormatBar forma={forma} keys={sel.keys} edit={editNoted} canEdit={editing} />
          </div>
        </header>

        <div className="dsg-body">
          <main className="dsg-main">
            <StudioWorkspace
              isDark={isDark}
              userId={userId || 'anon'}
              locked={dockLocked}
              forma={forma}
              edit={editNoted}
              canEdit={editing}
              toast={toast}
              sel={sel}
              pageVariant={effPageVariant}
              onPageVariant={setPageVariant}
              onDragState={setTokenDrag}
              dragActive={tokenDrag}
              floatingTools={floatingTools}
              onCloseFloating={closeFloating}
              onDockFloating={dockFloating}
              dropdown={dropdown}
              onCloseDropdown={() => setDropdown(null)}
              onFloatDropdown={floatTool}
              onUndock={undockToFloat}
              panelDragId={panelDragId}
              firmLayoutKey={STUDIO_FIRM_LAYOUT_KEY}
              onDockPanelsChange={setDockPanels}
              onOpenPanel={openPanelById}
              onDockReady={(h) => {
                dockHandleRef.current = h;
              }}
              canvas={(wiring) => (
                <StudioCanvas
                  paged={paged}
                  forma={forma}
                  zoom={zoom}
                  design={wiring}
                  sel={sel}
                  onLevelClick={openLevelStyle}
                  edit={editNoted}
                  peerSelections={draft.peerSelections}
                  onZoomDelta={(step) => setZoomPersisted(zoom + step)}
                />
              )}
            />
          </main>
        </div>
        <ConfirmDialog
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          title="Discard unpublished changes?"
          description="Leaving now throws away this new forma, which has not been published yet."
          confirmLabel="Discard"
          onConfirm={() => {
            setDiscardOpen(false);
            onBack();
          }}
        />
        <ConfirmDialog
          open={publishOpen}
          onOpenChange={setPublishOpen}
          title="Publish this forma?"
          description={publishConfirmBody(peerNames(draft.peers))}
          confirmLabel="Publish"
          onConfirm={() => {
            setPublishOpen(false);
            void runPublish();
          }}
        />
        <ConfirmDialog
          open={discardDraftOpen}
          onOpenChange={setDiscardDraftOpen}
          title="Discard this draft?"
          description={discardDraftBody(peerNames(draft.peers))}
          confirmLabel="Discard draft"
          onConfirm={() => {
            setDiscardDraftOpen(false);
            draft
              .discardDraft()
              .catch((err: unknown) =>
                toast(
                  'Could not discard the draft: ' +
                    (err instanceof Error ? err.message : String(err ?? 'unknown')),
                  true
                )
              );
          }}
        />
      </div>
    </ViewModeContext.Provider>
  );
}

export default Studio;
